// Reglas del proveedor de video (Enhancor/Seedance) que el panel de escena del
// Estudio tiene que respetar ANTES de mandar el pedido.
//
// Las dos que costaron incidentes seguidos (ADR-2026-09-10-contrato-de-enhancor-
// en-el-estudio): un prompt vacío devuelve `400 "Prompt is required"` y una
// duración fuera de 4–15s devuelve `400 "Duration must be between 4 and 15
// seconds"`. En los dos casos el intento se pierde entero: el usuario espera
// medio minuto y el clip queda en error.
//
// El backend recorta la duración (`clampSeedance2Duration`) y rechaza el prompt
// vacío (`creatorBody`), que es la capa que protege de verdad — cubre las builds
// viejas, los proyectos guardados y el agente. Esto es la capa de arriba, la que
// evita que el usuario apriete Generar contra algo que no puede salir bien.

/**
 * Piso de duración del proveedor. Es también el piso que ya ofrecen TODOS los
 * controles de duración del Estudio (el slider arranca en 4 y `DURATION_OPTIONS`
 * también): el único camino que dejaba pasar menos era restaurar la duración
 * desde el largo del clip en el timeline, que se podía recortar a mano.
 */
export const DURACION_MINIMA_ESCENA_S = 4;

/**
 * Duración de escena en segundos enteros y nunca por debajo del piso del
 * proveedor. `clip.duration` es la duración REAL medida al archivo generado, así
 * que trae decimales (4.0000012) — se redondea acá.
 */
export function clampDuracionDeEscena(segundos: number | undefined): number {
  const n = Math.round(Number(segundos));
  if (!Number.isFinite(n) || n <= 0) return DURACION_MINIMA_ESCENA_S;
  return Math.max(DURACION_MINIMA_ESCENA_S, n);
}

export interface EscenaParaPrompt {
  /** Modo del panel: `general` genera por Enhancor, `personas` por HeyGen. */
  sceneMode: string;
  prompt?: string;
  /** Multiescena: los prompts viajan en la lista de escenas, no en el top-level. */
  multiescena?: boolean;
}

/**
 * Espejo exacto de la regla del backend (`creatorBody`): el prompt es obligatorio
 * salvo en multiescena (los prompts van adentro de `escenas`) y en la rama de
 * personas (genera por HeyGen con el campo `dialogo`).
 */
export function faltaPromptDeEscena(e: EscenaParaPrompt): boolean {
  if (e.multiescena || e.sceneMode === 'personas') return false;
  return !e.prompt?.trim();
}

/** Lo mismo que dice el botón apagado, para que el clip en error no hable distinto. */
export const MOTIVO_SIN_PROMPT = 'Escribí qué querés que pase en la escena';

/**
 * Por qué un clip no se puede (re)generar, o `null` si se puede.
 *
 * El prompt va PRIMERO a propósito: es lo único que el usuario puede arreglar
 * solo. Antes los dos casos compartían el mismo texto —"falta información del
 * proyecto, probá recargar la página"— así que un clip guardado sin prompt
 * mandaba a recargar, recargar no cambiaba nada y el usuario quedaba trabado
 * (ADR-2026-09-10-contrato-de-enhancor-en-el-estudio).
 */
export function motivoDeClipSinDatos(
  e: EscenaParaPrompt & { tieneProyecto: boolean },
): string | null {
  if (faltaPromptDeEscena(e)) return MOTIVO_SIN_PROMPT;
  if (!e.tieneProyecto) {
    return 'No se pudo iniciar la generación: falta información del proyecto. Probá recargar la página.';
  }
  return null;
}
